import { withNamespaces } from "react-i18next";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { withRouter, useLocation } from "react-router-dom";
import OverviewHeader from "src/modules/data-protection/pages/admin/software/overview-header";
import Questionnaires from "./questionnaires";
import { useQuery } from "@tanstack/react-query";
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import { toast } from "react-toastify";
import { mergeComplianceObligations } from "src/modules/data-protection/helpers/complianceObligations";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";

const AIRiskAssessment = (props) => {
    const { t, assetId } = props;
    const location = useLocation();
    const overviewHeaderRef = useRef();

    const {
        data: softwareData,
        isFetching
    } = useQuery({
        queryKey: ["gdpr-software-basic-information", assetId],
        queryFn: async () => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.details(assetId);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t("An error occurred while fetching software details."), {
                type: "error"
            });
        }
    });

    const isCeConformityEnabled = isFeatureEnabledForActiveModule("ce-conformity");

    const complianceObligations = useMemo(() => {
        if (!softwareData) {
            return [];
        }

        // Merge AI Risk Assessment and CE Conformity obligation sources.
        // Unknown / future arrays are ignored safely when absent.
        return mergeComplianceObligations(
            softwareData.ai_act_obligations,
            isCeConformityEnabled ? softwareData.conformity_obligations : []
        );
    }, [softwareData, isCeConformityEnabled]);

    const onValidSubmit = useCallback(() => {
        if (overviewHeaderRef?.current) {
            overviewHeaderRef.current.reload();
        }
    }, []);

    useEffect(() => {
        if (!location.hash) {
            return;
        }

        const targetId = location.hash.replace("#", "");
        const element = document.getElementById(targetId);

        if (element) {
            element.scrollIntoView({
                behavior: "smooth",
                block: "start"
            });
        }
    }, [location.hash, isFetching]);

    return (
        <div className="p-4">
            <OverviewHeader
                assetId={assetId}
                actionsRef={overviewHeaderRef}
            />

            {!isFetching && softwareData && (
                <Questionnaires
                    onValidSubmit={onValidSubmit}
                    aiRiskAssessmentId={softwareData.ai_risk_assessment_report_id}
                    enableQuestionnaire={softwareData.has_ai_risk_assessment}
                    filledWithAIBefore={false}
                    isQuestionnaireCompleted={softwareData.ai_act_questionnaire_filled}
                    friaOptions={{
                        friaRequired: softwareData.fria_required,
                        hasFriaAssessment: softwareData.has_fria_assessment,
                        friaStatus: softwareData.fria_status,
                        friaReportCaseId: softwareData.fria_assessment_report_id
                    }}
                    conformityOptions={{
                        providerConformityRequired: isCeConformityEnabled && softwareData.provider_conformity_required,
                        providerConformityStatus: softwareData.provider_conformity_status,
                        conformityReportCaseId: softwareData.conformity_assessment_report_id
                    }}
                    providerConformityRequired={isCeConformityEnabled && softwareData.provider_conformity_required}
                    complianceObligations={complianceObligations}
                />
            )}
        </div>
    );
};

const mapStatetoProps = (state) => {
    const { token, user } = state.Login;
    const { Organization, App } = state;

    return {
        token,
        user,
        Organization,
        App
    };
};

export default withNamespaces()(withRouter(connect(mapStatetoProps, {})(AIRiskAssessment)));
